import { Component, Input, OnChanges } from '@angular/core';
import { LabelI18nService } from 'src/app/core/_services/label-i18n.service';
import * as Highcharts from 'highcharts';
import HC_exporting from 'highcharts/modules/exporting';
import HC_offlineExporting from 'highcharts/modules/offline-exporting';
import { Indicator } from 'src/app/core/_models/indicator';
import { ProjectEcoPro } from 'src/app/core/_models/projectEcoPro';
import { SolutionEcoPro } from 'src/app/core/_models/solutionEcoPro';
import { HIGHCHARTS_EXPORTING, HIGHCHARTS_TRANSLATIONS } from 'src/constants/graph.constants';

HC_exporting(Highcharts);
HC_offlineExporting(Highcharts);

let componentContext: any;

@Component({
  selector: 'app-bar-indicators-detailed-byStructureTypes',
  templateUrl: './bar-indicators-detailed-byStructureTypes.component.html',
  styleUrls: ['./bar-indicators-detailed-byStructureTypes.component.scss']
})
export class BarIndicatorsDetailedByStructureTypesComponent implements OnChanges {
  @Input() projectData: ProjectEcoPro = {};
  @Input() indicators: Indicator[] = [];

  indicatorComparatif: Indicator = {
    isChecked: false
  };

  updateFlag: boolean = false;
  totalsRef: number[] = [];

  // Highcharts config

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {
    chart: {
      type: 'column'
    },
    lang: HIGHCHARTS_TRANSLATIONS,
    title: {
      text: "Comparaison par catégorie d'aménagement et scénario"
    },
    exporting: HIGHCHARTS_EXPORTING,
    colors: ['#7cb5ec', '#434348', '#f7a35c', '#8085e9', '#f15c80', '#e4d354', '#2b908f', '#f45b5b', '#91e8e1'],
    xAxis: {
      crosshair: true
    },
    yAxis: {
      min: 0,
      title: {
        text: componentContext?.labelI18n.indicatorLabel(componentContext?.indicatorComparatif)
      }
    },
    tooltip: {
      headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
      pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' + '<td style="padding:0"><b>{point.y:.1f}</b></td></tr>',
      footerFormat: '</table>',
      shared: true,
      useHTML: true
    },
    plotOptions: {
      series: {
        dataLabels: [
          {
            enabled: true,
            useHTML: true,
            y: -25,
            crop: false,
            overflow: 'allow',
            formatter: function () {
              if (this.colorIndex == 0) {
                componentContext.totalsRef.splice(this.point.index, 0, this.y);
                return '<span style="background-color: lightgray; font-size: 10px; padding: 2px; border:1px solid"> Ref. </span>';
              } else {
                if (this.y && this.y != 0 && componentContext.totalsRef[this.point.index] != 0) {
                  const percentageDiff = (100 * (this.y - componentContext.totalsRef[this.point.index])) / componentContext.totalsRef[this.point.index];
                  if (!isNaN(percentageDiff)) {
                    const displayValue = percentageDiff == 0 ? '&nbsp' + '-' + '&nbsp' : percentageDiff > 0 ? '+' + Math.round(percentageDiff) + '%' : Math.round(percentageDiff) + '%';
                    const backgroundColor = percentageDiff < 0 ? '#25e043' : percentageDiff > 0 ? '#ff4c4c' : 'lightgray';

                    return '<span style="background-color:' + backgroundColor + '; font-size: 10px; padding: 2px; border:1px solid">' + displayValue + '</span>';
                  } else {
                    return '';
                  }
                } else {
                  return '';
                }
              }
            }
          },
          {
            enabled: true,
            allowOverlap: true,
            format: '{point.y}',
            crop: false,
            overflow: 'allow'
          }
        ]
      },
      column: {
        pointPadding: 0.2,
        borderWidth: 0
      }
    }
  };

  constructor(public labelI18n: LabelI18nService) {
    componentContext = this;
  }

  ngOnChanges() {
    if (this.indicatorComparatif) this.formatDataForGraph();
  }

  selectIndicatorChange(indicatorId: any) {
    let i: Indicator | undefined = this.indicators?.find((indicator: Indicator) => indicator.indicatorId == indicatorId);
    if (i) {
      this.indicatorComparatif = i;
      this.formatDataForGraph();
    }
  }

  formatDataForGraph() {
    let series: { type: string; name: string | undefined; data: number[] }[] = [];
    let solutionsData: SolutionEcoPro[] = [];
    let structuresCategoriesNames: string[] = [];

    this.projectData.solutionsEcoPro?.forEach((solution) => {
      solution.structureTypesProject.forEach((structureTypeProject) => {
        if (structureTypeProject.structureType.displayName && !structuresCategoriesNames.includes(structureTypeProject.structureType.displayName)) {
          structuresCategoriesNames.push(structureTypeProject.structureType.displayName);
        }
      });
    });

    this.projectData.solutionsEcoPro?.forEach((solution) => {
      structuresCategoriesNames.forEach((structureCategoryName) => {
        let ceuValueForStructureType = 0;

        const structureTypeProjectRef = solution.structureTypesProject.find((structureTypeProject) => structureTypeProject.structureType.displayName == structureCategoryName);

        if (structureTypeProjectRef != null) {
          ceuValueForStructureType = +structureTypeProjectRef.impactsCEU?.find((impact) => impact.displayName == this.indicatorComparatif.displayName)?.value!.toFixed(2)!;
        }

        let solutionRef = solutionsData.find((solutionData) => solutionData.solutionEcoProId == solution.solutionEcoProId);

        if (solutionRef == null) {
          solutionsData.push({ solutionEcoProId: solution.solutionEcoProId, displayName: solution.displayName, dataForGraph: [ceuValueForStructureType], structureTypesProject: [] });
        } else {
          solutionRef.dataForGraph?.push(ceuValueForStructureType);
        }
      });
    });

    solutionsData.forEach((solutionData) => {
      if (solutionData.dataForGraph) {
        series.push({
          type: 'column',
          name: solutionData.displayName,
          data: solutionData.dataForGraph
        });
      }
    });

    this.chartOptions.xAxis = {
      categories: structuresCategoriesNames
    };

    this.chartOptions.yAxis = {
      title: {
        text: componentContext?.labelI18n.indicatorLabelWithUnit(componentContext?.indicatorComparatif)
      }
    };

    this.chartOptions.series = series as Highcharts.SeriesOptionsType[];

    componentContext.totalsRef = [];

    structuresCategoriesNames.forEach(() => {
      componentContext.totalsRef.push(0);
    });

    this.updateFlag = true;
  }
}
